import axiosInstance from './Instance/axiosInstance';
import { ReportData } from '../types/report';

export const fetchReport = async (startDate: string, endDate: string, customerCode = ''): Promise<ReportData> => {
    const res = await axiosInstance.get(`/orders/report?startDate=${startDate}&endDate=${endDate}&customerCode=${customerCode}`);
    return res.data;
};

export const fetchDailySummary = (params: { startDate: string; endDate: string }) =>
    axiosInstance.get('/reports/daily-summary', { params }).then(res => res.data);

export type RankSortBy = 'qty' | 'amount';
export type RankOrder = 'asc' | 'desc';

export interface RankedItem {
    _id: string;
    totalQty: number;
    totalAmount: number;
    /** What the goods cost, from the product's buy price. */
    totalCost: number;
    /** Amount less cost. Negative on returns — a return gives the profit back. */
    totalProfit: number;
    invoices: number;
    product: { _id: string; name: string; code: string; category?: string };
}

export const fetchTopItems = (params: {
    startDate: string;
    endDate: string;
    type: string;
    limit?: number;
    sortBy?: RankSortBy;
    order?: RankOrder;
}) => axiosInstance.get('/reports/top-items', { params }).then(res => res.data);

/** Best selling items, ranked by quantity or by money. */
export const fetchBestSelling = (params: {
    startDate: string;
    endDate: string;
    limit?: number;
    sortBy?: RankSortBy;
    order?: RankOrder;
}): Promise<RankedItem[]> =>
    axiosInstance.get('/reports/best-selling', { params }).then(res => res.data);

/** Most returned items, ranked the same way. */
export const fetchMostReturned = (params: {
    startDate: string;
    endDate: string;
    limit?: number;
    sortBy?: RankSortBy;
    order?: RankOrder;
}): Promise<RankedItem[]> =>
    axiosInstance.get('/reports/most-returned', { params }).then(res => res.data);

export type ItemReportInvoiceType = 'SALE' | 'RETURN' | 'BUY';

export interface ItemReportRow {
    invoiceType: ItemReportInvoiceType;
    invoiceId: string;
    invoiceNumber: string;
    date: string;
    currency: string;
    party: string | null;
    unit: string;
    qty: number;
    cost: number;
    price: number;
    profit: number;
    note?: string | null;
}

export interface ItemReportBucket {
    qty: number;
    amount: number;
    cost: number;
    profit: number;
    invoices: number;
}

export interface ItemReportChartPoint {
    month: string;
    saleQty: number;
    returnQty: number;
    purchaseQty: number;
    saleAmount: number;
    returnAmount: number;
    purchaseAmount: number;
    profit: number;
}

export interface ItemReportData {
    product: {
        id: string;
        code: string;
        name: string;
        category: string | null;
        sku: string | null;
        barcode: string | null;
        buyPriceInDinar: number;
        buyPriceInDollar: number;
        sellPriceInDinar: number;
        sellPriceInDollar: number;
    };
    period: { startDate: string | null; endDate: string | null };
    filterType: 'all' | 'sale' | 'return' | 'purchase';
    rows: ItemReportRow[];
    totalsByCurrency: Record<string, {
        sale: ItemReportBucket;
        return: ItemReportBucket;
        purchase: ItemReportBucket;
        netProfit: number;
    }>;
    chart: ItemReportChartPoint[];
    stock: {
        countedFrom: string | null;
        byUnit: Record<string, {
            initial: number;
            bought: number;
            sold: number;
            returned: number;
            final: number;
        }>;
        currentStock: number;
        valueIQD: number;
        valueUSD: number;
    };
}

/** Everything that ever happened to one item, with chart data and totals. */
export const fetchItemReport = (params: {
    productId?: string;
    code?: string;
    startDate?: string;
    endDate?: string;
    type?: 'all' | 'sale' | 'return' | 'purchase';
}): Promise<ItemReportData> =>
    axiosInstance.get('/reports/item-report', { params }).then(res => res.data);

export const fetchTopCustomers = (params: { startDate: string; endDate: string; limit?: number }) =>
    axiosInstance.get('/reports/top-customers', { params }).then(res => res.data);

export const fetchTopSuppliers = (params: { startDate: string; endDate: string; limit?: number }) =>
    axiosInstance.get('/reports/top-suppliers', { params }).then(res => res.data);

export const fetchYearlySalesChart = (params: { year: number }) =>
    axiosInstance.get('/reports/yearly-sales-chart', { params }).then(res => res.data);

export const fetchDashboard = () =>
    axiosInstance.get('/reports/dashboard').then(res => res.data);

export const fetchStockReport = () =>
    axiosInstance.get('/reports/stock-report').then(res => res.data);

export const fetchProfitReport = (params: { startDate: string; endDate: string }) =>
    axiosInstance.get('/reports/profit-report', { params }).then(res => res.data);
export const fetchStockValueReport = () =>
    axiosInstance.get('/reports/product-stock-value-report').then(res => res.data);

export interface RecalcUnitDiff {
    before: number;
    after: number;
    delta: number;
}

export interface RecalcChange {
    id: string;
    code: string;
    name: string;
    countFromBefore: string | null;
    countFromAfter: string | null;
    units: Record<string, RecalcUnitDiff>;
}

export interface RecalcResult {
    /** false when this was a preview — nothing was written. */
    applied: boolean;
    confirmRequired: boolean;
    from: string | null;
    to: string | null;
    productsTotal: number;
    productsChanged: number;
    unitsChanged: number;
    /** Reference for undoing an applied recalculation. */
    snapshotBatch: string | null;
    changes: RecalcChange[];
}

/**
 * Re-baselines stock against invoice history.
 * Omit `confirm` (or pass false) to preview the difference without writing.
 * `from: null` counts the entire invoice history, ignoring any reset baseline.
 */
export const recalculateProductStock = (params?: {
    from?: string | null;
    to?: string | null;
    confirm?: boolean;
}): Promise<RecalcResult> =>
    axiosInstance.post('/reports/recalculate-product-stock', params || {}).then(res => res.data);